/**
 * おすすめ求人（Workgate + Carat 混在）のブロック割り。
 *
 * varuna の get_recommend_jobs は「提供元ごとにマッチ度降順で並べたリスト」を
 * 平均スコアの高い提供元から順に返す。何件ごとに提供元を切り替えるかは
 * 表示側の列数に依存するため、その決定はここで行う。
 *
 * ブロックは提供元ごとに独立した描画単位になる。1つのグリッドに全件流して
 * 件数で提供元を切り替えると、列数が変わった瞬間に境界がズレて行内で
 * 提供元が混ざるため、混ざらないことは構造で保証する。
 */
import { computed, onBeforeUnmount, ref, unref } from 'vue'
import type { Ref } from 'vue'

/**
 * assets/scss/variables.scss の $mq-sm と一致させる。
 * ここがズレるとブロックサイズと実際の列数が食い違い、行内で提供元が混ざる。
 */
const MOBILE_MEDIA_QUERY = '(max-width: 661px)'

const GRID_COLUMNS_DESKTOP = 3
const GRID_COLUMNS_MOBILE = 2

/** 縦1列リスト（ダイアログ）は1件ずつ交互にする */
const LIST_BLOCK_SIZE = 1

/**
 * Carat 応募時に送る「応募者の区分」（applicant.occupation にそのまま転送される）。
 *
 * 求人検索用の雇用形態（アルバイト / 正社員の2値）とは別物。既存の
 * CaratDialog.appCarat が正本で、パート・大学生を区別する。検索用の
 * getOccupationByFormat を流用すると応募者情報が誤る。
 */
export function toApplicantOccupation(formatId: string): string {
  if (formatId === 'rerekisho_part_time') return 'アルバイト'
  if (formatId === 'rirekisho_part_time2') return 'パート'
  if (
    formatId === 'rirekisho_job_new' ||
    formatId === 'rirekisho_job_new_single' ||
    formatId === 'internship'
  ) {
    return '大学生'
  }
  return '正社員'
}

/**
 * Carat の重複判定グループキー。job_id の先頭セグメント（`120-43425242` → `120`）。
 *
 * Carat は「同じ求人または同じ会社の求人に応募済です」（DUPLICATE_APPLICATION）を
 * この単位で返す。実測では `120-` の9件が1件の応募以降すべて弾かれる一方 `17-` は
 * 通った。リストに並ぶのは施設ごとに別名の会社名なので company_name では束ねられない。
 * プレフィックスが何の識別子か（クライアント/法人/媒体）は提供元に確認中。
 *
 * 数字のみの先頭セグメントに限る。実データは全て数字で、想定外の id 形式
 * （全件が同じ文字列プレフィックスを持つ等）で束ねすぎると、ダイアログは
 * hide-applied なので1件の応募で一覧が空になる。判定できないときは '' を返し、
 * 呼び出し側を完全一致だけの従来動作に落とす。
 */
export function caratDuplicateGroupKey(
  jobId: string | number | null | undefined
): string {
  const id = String(jobId == null ? '' : jobId)
  const index = id.indexOf('-')
  if (index <= 0) return ''
  const prefix = id.slice(0, index)
  return /^[0-9]+$/.test(prefix) ? prefix : ''
}

export type RecommendLayout = 'mypage' | 'dialog'

export interface RecommendOffer {
  provider: string
  job_id: string
  title: string
  company_name: string
  pref_name: string
  city_name: string
  location_text: string
  salary_text: string
  employment_types: string[]
  features: string[]
  image_url: string
  snippet: string
  detail_url: string
  apply_url: string
  search_id: string
  is_applied: boolean
  /** false のとき応募ボタンを出さない（本番の Carat は応募APIが止まっている） */
  apply_enabled: boolean
  match_score: number
}

export interface RecommendProviderGroup {
  provider: string
  avg_score: number
  offers: RecommendOffer[]
}

export interface PositionedOffer extends RecommendOffer {
  /** 枠全体での通し順位（1始まり）。応募ログの display_position に送る */
  displayPosition: number
  blockIndex: number
}

export interface RecommendBlock {
  provider: string
  /** 1始まり（0 は応募ログ側で「未計測」を意味するため使わない） */
  blockIndex: number
  offers: PositionedOffer[]
}

/** 提供元キューから blockSize 件ずつ交互に取り出してブロック列を作る */
function buildRecommendBlocks(
  groups: RecommendProviderGroup[] | null | undefined,
  blockSize: number
): RecommendBlock[] {
  const size = Math.max(1, Math.floor(blockSize) || 1)
  const queues = (groups || [])
    .filter(
      (group) => group && Array.isArray(group.offers) && group.offers.length > 0
    )
    .map((group) => ({
      provider: group.provider,
      offers: group.offers.slice()
    }))

  const blocks: RecommendBlock[] = []
  let position = 1
  // 1始まり。応募APIと履歴テーブルでは 0 が「未送信・未計測」の既定値なので、
  // 先頭ブロックに 0 を振ると既存の未計測レコードと区別できなくなる
  let blockIndex = 1

  // 全キューが空になるまで1周ずつ取る。片方が尽きたら残りは連続して出る（穴埋め）
  while (queues.some((queue) => queue.offers.length > 0)) {
    let consumed = false
    for (const queue of queues) {
      if (queue.offers.length === 0) continue
      const chunk = queue.offers.splice(0, size)
      const currentBlockIndex = blockIndex
      blocks.push({
        provider: queue.provider,
        blockIndex: currentBlockIndex,
        offers: chunk.map((offer) => ({
          ...offer,
          displayPosition: position++,
          blockIndex: currentBlockIndex
        }))
      })
      blockIndex++
      consumed = true
    }
    // splice が必ず1件以上減らすため通常ここには来ないが、
    // 想定外の入力で無限ループにしないための保険
    if (!consumed) break
  }

  return blocks
}

/**
 * 正規化スキーマの求人を、既存の求人詳細モーダルが参照するフィールド名に寄せる。
 *
 * 詳細モーダル（MyPage の workgate-detail-dialog / WorkgateDialog の Step2）は
 * Workgate API の生レスポンス形（company.name / salary.full_text / employ_name）を
 * 前提に組まれているため、正規化スキーマをそのまま渡すと空表示になる。
 */
/**
 * Carat の `job` から募集職種の表示名を作る。
 *
 * `job` は3形に揺れる（本番の応募実績3,000件で実測）:
 *   - 自由記述      88.2%  例) '施工管理' '販売スタッフ'
 *   - カテゴリ階層   6.5%  例) '医療/福祉 > 介護/ヘルパー'
 *   - 'その他'       5.3%
 * 階層形をそのまま出すと親カテゴリまで並ぶので末尾だけ使う。
 * 'その他' は提供元が職種として送っている値なので落とさない。
 */
export function trimCaratJobName(job: unknown): string {
  const text = typeof job === 'string' ? job.trim() : ''
  if (!text.includes('>')) return text
  const tail = (text.split('>').pop() || '').trim()
  // 'A>' のように末尾が空なら元の文字列を残す（情報を消さない）
  return tail || text
}

export function toRecommendDetailShape(
  offer: RecommendOffer | null | undefined
): Record<string, unknown> | null {
  if (!offer || typeof offer !== 'object') return null
  const positioned = offer as PositionedOffer
  return {
    // 詳細画面からそのまま応募できるよう、識別子と計測用の順位も持ち回る
    provider: offer.provider || '',
    job_id: offer.job_id || '',
    search_id: offer.search_id || '',
    employment_types: offer.employment_types || [],
    displayPosition: positioned.displayPosition || 0,
    blockIndex: positioned.blockIndex || 0,
    apply_enabled: offer.apply_enabled !== false,
    // 詳細画面でも「応募済み」に切り替えられるようキーを持たせる
    // （後から代入してもリアクティブになるよう、必ず定義しておく）
    is_applied: offer.is_applied === true,
    title: offer.title || '',
    concise_title: offer.title || '',
    // 募集職種（job_name）は埋めない。雇用形態を入れると「募集職種: 正社員」と
    // 表示され、直下の employ_name と重複した上に職種として不正確になる。
    // 職種情報を持たない提供元では '-' のままにする
    company: { name: offer.company_name || '' },
    // 所在地は location_text に都道府県・市区町村まで入っているため、
    // pref_name / city_name を重ねると「東京都 新宿区 東京都(新宿区)」になる。
    // 表示は flat な1本（place）に寄せる
    pref_name: '',
    city_name: '',
    place: offer.location_text || '',
    salary: { full_text: offer.salary_text || '' },
    employ_name: (offer.employment_types || []).join(' / '),
    job_description: offer.snippet || '',
    access: '',
    detail_url: offer.detail_url || ''
  }
}

/**
 * 画面幅がモバイル相当かを追跡する。
 *
 * VueUse の `useMediaQuery` は使わない。この構成（Vue 2.7.16 + @vueuse/core 11）
 * では setup 内（`getCurrentInstance()` あり）で呼ぶと例外を投げ、Vue 2.7 の
 * `initSetup` が `invokeWithErrorHandling` で握りつぶすため setup の戻り値が
 * まるごと undefined になる（テンプレートから何も見えなくなる）。
 * インスタンス外で呼ぶと成功するので発見しにくい。
 */
function useIsMobileWidth(): Ref<boolean> {
  const isMobile = ref(false)
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return isMobile
  }
  const mql = window.matchMedia(MOBILE_MEDIA_QUERY)
  isMobile.value = mql.matches
  const onChange = (event: MediaQueryListEvent) => {
    isMobile.value = event.matches
  }
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', onChange)
    onBeforeUnmount(() => mql.removeEventListener('change', onChange))
  } else if (typeof mql.addListener === 'function') {
    // Safari 13 以下
    mql.addListener(onChange)
    onBeforeUnmount(() => mql.removeListener(onChange))
  }
  return isMobile
}

export function useRecommendJobBlocks(
  groups: Ref<RecommendProviderGroup[]>,
  layout: RecommendLayout
) {
  const isMobile = useIsMobileWidth()

  const blockSize = computed(() => {
    if (layout === 'dialog') return LIST_BLOCK_SIZE
    return isMobile.value ? GRID_COLUMNS_MOBILE : GRID_COLUMNS_DESKTOP
  })

  const blocks = computed(() =>
    buildRecommendBlocks(unref(groups), blockSize.value)
  )

  const totalCount = computed(() =>
    blocks.value.reduce((sum, block) => sum + block.offers.length, 0)
  )

  return { blocks, blockSize, isMobile, totalCount }
}
